import { Star, RotateCcw, Check } from 'lucide-react';

export function EquationBuilder({ slots, currentAnswer, feedback, onSlotClick, onClear, onCheck }) {
  const allFilled = slots[0] && slots[1] && slots[2];

  return (
    <div
      data-testid="equation-builder"
      className={`glass-panel p-4 sm:p-5 ${feedback === 'wrong' ? 'animate-shake' : ''}`}
    >
      <div className="flex items-center justify-center gap-2 sm:gap-3 mb-4">
        {/* Slot 0: First Number */}
        <button
          data-testid="drop-zone-0"
          onClick={() => onSlotClick(0)}
          className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-xl sm:text-2xl font-black transition-all duration-300
            ${slots[0] ? `${slots[0].color} text-white shadow-lg animate-pop-in` : 'border-4 border-dashed border-amber-300 bg-amber-50/50 text-amber-400'}`}
        >
          {slots[0] ? slots[0].display : '?'}
        </button>

        {/* Slot 1: Operation */}
        <button
          data-testid="drop-zone-1"
          onClick={() => onSlotClick(1)}
          className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-2xl sm:text-3xl font-black transition-all duration-300
            ${slots[1] ? `${slots[1].color} text-white shadow-lg animate-pop-in` : 'border-4 border-dashed border-amber-300 bg-amber-50/50 text-amber-400'}`}
        >
          {slots[1] ? slots[1].display : '?'}
        </button>

        {/* Slot 2: Second Number */}
        <button
          data-testid="drop-zone-2"
          onClick={() => onSlotClick(2)}
          className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-xl sm:text-2xl font-black transition-all duration-300
            ${slots[2] ? `${slots[2].color} text-white shadow-lg animate-pop-in` : 'border-4 border-dashed border-amber-300 bg-amber-50/50 text-amber-400'}`}
        >
          {slots[2] ? slots[2].display : '?'}
        </button>

        <span className="font-heading text-3xl sm:text-4xl font-black text-amber-950">=</span>

        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-yellow-400 border-4 border-yellow-500 flex items-center justify-center shadow-lg">
          <span className="font-heading text-xl sm:text-2xl font-black text-amber-950">{currentAnswer}</span>
        </div>
      </div>

      <div className="flex justify-center gap-3">
        <button
          data-testid="clear-equation"
          onClick={onClear}
          className="bg-gray-400 text-white font-bold text-sm sm:text-base py-2.5 px-5 rounded-full border-b-[4px] border-gray-600 active:border-b-0 active:translate-y-[4px] transition-all flex items-center gap-2 hover:bg-gray-500"
        >
          <RotateCcw className="w-4 h-4" />
          Clear
        </button>
        <button
          data-testid="submit-equation"
          onClick={onCheck}
          disabled={!allFilled}
          className={`font-bold text-sm sm:text-base py-2.5 px-5 rounded-full border-b-[4px] transition-all flex items-center gap-2
            ${allFilled
              ? 'bg-orange-500 text-white border-orange-700 active:border-b-0 active:translate-y-[4px] hover:bg-orange-400'
              : 'bg-gray-300 text-gray-500 border-gray-400 cursor-not-allowed'}`}
        >
          <Check className="w-4 h-4" />
          Check!
        </button>
      </div>

      {feedback === 'correct' && (
        <div data-testid="feedback-correct" className="text-center mt-3 animate-pop-in">
          <span className="font-heading text-2xl font-black text-emerald-600 flex items-center justify-center gap-2">
            <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
            Awesome!
            <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
          </span>
        </div>
      )}
      {feedback === 'wrong' && (
        <div data-testid="feedback-wrong" className="text-center mt-3">
          <span className="font-heading text-2xl font-black text-red-500">Try Again!</span>
        </div>
      )}
    </div>
  );
}

export function GameTimer({ timeLeft, totalTime }) {
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const timerPercentage = (timeLeft / totalTime) * 100;
  const timerColor = timerPercentage > 50 ? 'bg-emerald-500' : timerPercentage > 25 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div className="mb-3">
      <div className="flex justify-between mb-1">
        <span className="font-body text-xs font-bold text-amber-900">Time Left</span>
        <span
          data-testid="timer-display"
          className={`font-heading text-base font-black text-amber-950 ${timeLeft <= 30 ? 'timer-low text-red-600' : ''}`}
        >
          {formatTime(timeLeft)}
        </span>
      </div>
      <div className="w-full h-3 bg-white/50 rounded-full overflow-hidden border-2 border-white">
        <div
          data-testid="timer-bar"
          className={`h-full ${timerColor} rounded-full transition-all duration-1000 ease-linear`}
          style={{ width: `${timerPercentage}%` }}
        />
      </div>
    </div>
  );
}
